/**
 * Postcode Validation System
 * Handles header postcode validation and modal interactions
 * Works with custom Shopify app API for postcode validation
 */

class PostcodeValidator {
  constructor() {
    // Skip initialization on cart page to avoid duplicate UX
    if (window.location.pathname.includes('/cart')) {
      console.log('Postcode validator - Skipping initialization on cart page');
      return;
    }

    this.form = document.getElementById('postcode-form');
    this.input = document.getElementById('postcode-input');
    this.submitButton = document.querySelector('#postcode-form button[type="submit"]');
    this.submitText = document.querySelector('.postcode-submit__text');
    this.submitLoading = document.querySelector('.postcode-submit__loading');
    this.result = document.getElementById('postcode-result');
    this.resultMessage = document.querySelector('.postcode-result__message');
    this.successIcon = document.querySelector('.postcode-result__icon--success');
    this.errorIcon = document.querySelector('.postcode-result__icon--error');
    this.postcodeDisplay = document.getElementById('postcode-display');
    this.postcodeDisplayMobile = document.getElementById('postcode-display-mobile');
    this.modal = document.getElementById('PostcodeModal');
    
    this.init();
  }

  init() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      if (this.input) {
        this.input.addEventListener('input', this.handleInput.bind(this));
      }
      this.loadSavedPostcode();
      this.setupModalClose();
      this.setupModalOpener();
    }
  }

  handleInput(event) {
    const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    event.target.value = value;
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const postcode = this.input.value.trim();
    if (!postcode) return;

    this.setLoading(true);
    this.hideResult();

    try {
      // Validate postcode using custom app API
      const validationResult = await this.validatePostcodeAPI(postcode);
      
      if (validationResult.valid) {
        this.showSuccess('Delivery available to this postcode!', postcode);
        this.savePostcode(postcode);
      } else {
        this.showError('Sorry, we don\'t deliver to this postcode yet.');
      }
    } catch (error) {
      console.error('Postcode validation error:', error);
      this.showError('Unable to check postcode. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async validatePostcodeAPI(postcode) {
    try {
      console.log('Postcode validator - Validating postcode via API:', postcode);
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      
      const requestBody = { 
        postcode: postcode,
        pathway: 'internal__postcode_validation'
      };
      
      const response = await fetch('/apps/logistics/windows', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      // Check Content-Type header to handle HTML errors
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Postcode validator - Non-JSON response:', text.substring(0, 200));
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Postcode validator - API response:', data);
      
      // Expect response format: { valid: true } or { valid: false }
      return {
        valid: data.valid === true
      };
    } catch (error) {
      console.error('Postcode validator - API validation error:', error);
      return { valid: false };
    }
  }

  setLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      if (loading) {
        this.submitButton.style.opacity = '0.5';
      } else {
        this.submitButton.style.opacity = '1';
      }
    }
    if (this.submitText) {
      this.submitText.style.display = loading ? 'none' : 'inline';
    }
    if (this.submitLoading) {
      this.submitLoading.style.display = loading ? 'flex' : 'none';
    }
  }

  showSuccess(message, postcode) {
    if (this.result) {
      this.result.classList.remove('postcode-result--error');
      this.result.classList.add('postcode-result--success');
      this.result.style.display = 'block';
    }
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
    }
    this.updatePostcodeDisplay(postcode);
  }

  showError(message) {
    if (this.result) {
      this.result.classList.remove('postcode-result--success');
      this.result.classList.add('postcode-result--error');
      this.result.style.display = 'block';
    }
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
    }
  }

  hideResult() {
    if (this.result) {
      this.result.style.display = 'none';
    }
  }

  savePostcode(postcode) {
    localStorage.setItem('validated_postcode', postcode);
    
    // Dispatch custom event for delivery widget
    window.dispatchEvent(new CustomEvent('postcodeValidated', {
      detail: { postcode: postcode }
    }));
  }

  loadSavedPostcode() {
    const savedPostcode = localStorage.getItem('validated_postcode');
    if (savedPostcode && this.input) {
      this.input.value = savedPostcode;
      this.updatePostcodeDisplay(savedPostcode);
    }
  }

  updatePostcodeDisplay(postcode) {
    if (this.postcodeDisplay) {
      this.postcodeDisplay.textContent = postcode;
    }
    if (this.postcodeDisplayMobile) {
      this.postcodeDisplayMobile.textContent = postcode;
    }
  }

  setupModalClose() {
    if (this.modal) {
      // Close button
      const closeButton = this.modal.querySelector('.postcode-modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeModal());
      }

      // Click outside to close
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isModalOpen()) {
          this.closeModal();
        }
      });
    }
  }

  setupModalOpener() {
    // Find all elements with data-modal attribute pointing to PostcodeModal
    const openers = document.querySelectorAll('[data-modal="#PostcodeModal"]');
    openers.forEach(opener => {
      opener.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });
  }

  openModal() {
    if (this.modal) {
      // Use modal-dialog open attribute if available
      if (this.modal.setAttribute) {
        this.modal.setAttribute('open', '');
      }
      // Fallback: use class and display
      this.modal.classList.add('open');
      this.modal.style.display = 'flex';
      this.modal.style.visibility = 'visible';
      
      // Focus input
      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
    }
  }

  closeModal() {
    if (this.modal) {
      // Remove open attribute
      if (this.modal.removeAttribute) {
        this.modal.removeAttribute('open');
      }
      // Remove class and hide
      this.modal.classList.remove('open');
      this.modal.style.display = 'none';
      this.modal.style.visibility = 'hidden';
    }
  }

  isModalOpen() {
    if (!this.modal) return false;
    return this.modal.hasAttribute('open') || 
           this.modal.classList.contains('open') ||
           this.modal.style.display === 'flex';
  }

  debugModalOpener() {
    console.log('Postcode validator - Modal opener setup complete');
    console.log('Postcode validator - Modal element:', this.modal);
    console.log('Postcode validator - Form element:', this.form);
  }
}

/**
 * Legacy Metafield Support (Deprecated)
 * This function is kept for backward compatibility but is no longer used
 * Postcode validation now uses the custom app API
 */
function loadPostcodeMetafields(metafieldData) {
  console.warn('loadPostcodeMetafields is deprecated - postcode validation now uses API');
  // Keep for backward compatibility but don't use
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PostcodeValidator();
});

// Make functions globally available
window.PostcodeValidator = PostcodeValidator;
window.loadPostcodeMetafields = loadPostcodeMetafields;

